// src/pages/AboutUsPage.js
import React, { useState, useEffect } from "react";
import axiosInstance from "../api/axiosInstance";
import PagesTopNav from "../components/TopNav/PagesTopNav";
import { useAuth } from "../context/AuthContext";
import { FaEdit, FaSave, FaTimes } from "react-icons/fa";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import PageLayout from "../components/PageLayout";
import styled from "styled-components";

// (Removed ContentContainer)

// --- Editors ---
const EditContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

const EditTextarea = styled.textarea`
  width: 100%;
  min-height: 200px;
  padding: 1rem;
  border: 1px solid #ddd;
  border-radius: 8px;
  font-size: 1rem;
  line-height: 1.6;
  resize: vertical;
  transition: all .3s ease;
  &:focus {
    border-color: #4a6cf7;
    box-shadow: 0 0 0 2px rgba(74,108,247,.2);
    outline: none;
  }
`;

const EditControls = styled.div`
  display: flex;
  gap: 1rem;
  justify-content: flex-end;
`;

const IconButton = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  color: #4a6cf7;
  font-size: 1.2rem;
  transition: all .2s ease;
  padding: .5rem;
  border-radius: 50%;
  &:hover { background: rgba(74,108,247,.1); transform: scale(1.1); }
  &.cancel { color: #ff4757; }
`;

// --- Content ---
const ContentDisplay = styled.div`
  position: relative;
  padding: 1rem 0;   /* rely on PageLayout spacing; keep light padding only */
  line-height: 1.8;
  font-size: 1.1rem;
  color: #333;
  p { white-space: pre-wrap; }
`;

const EditIcon = styled(FaEdit)`
  position: absolute;
  top: .5rem;
  right: .5rem;
  cursor: pointer;
  color: #4a6cf7;
  transition: all .2s ease;
  &:hover { transform: scale(1.1); }
`;

// Inline alert (plain JS). Use a transient prop `$variant`.
const InlineAlert = styled.div`
  margin: 0 0 1rem;
  padding: 10px 12px;
  border-radius: 8px;
  font-size: .95rem;
  border: 1px solid ${(p) => (p.$variant === "error" ? "#fbd5d5" : "#c7e2ff")};
  background: ${(p) => (p.$variant === "error" ? "#fde8e8" : "#eef6ff")};
  color: ${(p) => (p.$variant === "error" ? "#9b1c1c" : "#1e3a8a")};
`;

const AboutUsPage = () => {
  const label = "about-us";
  const [content, setContent] = useState("Loading…");
  const [editing, setEditing] = useState(false);
  const [editedContent, setEditedContent] = useState("");
  const [loadError, setLoadError] = useState("");

  const { authData } = useAuth();
  const canEdit = authData?.role === "SystemAdmin";

  useEffect(() => {
    let mounted = true;
    axiosInstance
      .get(`/PageContent/${label}`)
      .then((res) => {
        if (!mounted) return;
        setLoadError("");
        setContent(
          res.data?.content ??
            "Welcome to Nnetego Hotel Management System.\n\n" +
            "We are a leading provider of innovative hotel management solutions..."
        );
      })
      .catch((err) => {
        console.error("Error fetching About Us content:", err);
        if (!mounted) return;
        setLoadError("We’re having trouble loading this section right now.");
      });
    return () => { mounted = false; };
  }, [label]);

  const handleEdit = () => { setEditedContent(content); setEditing(true); };
  const handleCancel = () => { setEditing(false); setEditedContent(""); };

  const handleSave = async (e) => {
    e.preventDefault();
    try {
      await axiosInstance.put(
        `/PageContent/${label}`,
        { content: editedContent },
        { headers: { Authorization: `Bearer ${authData?.token}`, "Content-Type": "application/json" } }
      );
      setContent(editedContent);
      setEditing(false);
      if (canEdit) toast.success("Content updated successfully!");
    } catch (error) {
      console.error(`Error updating content for label ${label}:`, error);
      if (canEdit) toast.error("Failed to update content.");
    }
  };

  return (
    <div>
      <PagesTopNav />
      {canEdit && <ToastContainer />}

      <PageLayout title="About Us" description="Learn more about our company and mission.">
        {/* ⬇️ No wrapper after content-section */}
        {loadError && (
          <InlineAlert role="status" aria-live="polite" $variant="error">
            {loadError}
          </InlineAlert>
        )}

        {editing ? (
          <EditContainer>
            <EditTextarea
              value={editedContent}
              onChange={(e) => setEditedContent(e.target.value)}
              rows={10}
              aria-label="Edit About Us Content"
            />
            <EditControls>
              <IconButton onClick={handleSave} aria-label="Save"><FaSave /></IconButton>
              <IconButton onClick={handleCancel} className="cancel" aria-label="Cancel"><FaTimes /></IconButton>
            </EditControls>
          </EditContainer>
        ) : (
          <ContentDisplay>
            <p>{content}</p>
            {canEdit && <EditIcon onClick={handleEdit} title="Edit" aria-label="Edit" />}
          </ContentDisplay>
        )}
      </PageLayout>
    </div>
  );
};

export default AboutUsPage;
